import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Save } from 'lucide-react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { PracticeMainFields } from '../../components/training/practice/PracticeMainFields';
import { PracticeStatusFields } from '../../components/training/practice/PracticeStatusFields';
import { PracticeHistory } from '../../components/training/practice/PracticeHistory';
import { PracticeScheduling } from '../../components/training/practice/PracticeScheduling';
import { PracticeBillingFields } from '../../components/training/practice/PracticeBillingFields';
import { ProspectConfirmModal } from '../../components/training/practice/ProspectConfirmModal';
import { usePractices } from '../../hooks/usePractices';
import { practicesStore } from '../../services/practicesStore';
import { mockData } from '../../services/mockData';
import { duplicatePractice, emptyPracticeDraft, isInPresence, requesterName } from '../../utils/practices';
import { PracticeDraft, PracticeErrors, PracticeStatus } from '../../types/practice';
export function PracticeEditorPage() {
  const {
    practiceId
  } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    practices
  } = usePractices();
  const existing = practiceId ? practices.find(p => p.id === practiceId) : undefined;
  const source = practices.find(p => p.id === searchParams.get('duplica'));
  const [draft, setDraft] = useState<PracticeDraft>(() => existing ? {
    ...existing
  } : source ? duplicatePractice(source) : emptyPracticeDraft());
  const [errors, setErrors] = useState<PracticeErrors>({});
  const [prospectName, setProspectName] = useState<string | null>(null);
  const [activatedNotice, setActivatedNotice] = useState('');
  if (practiceId && !existing) return <Navigate to="/training/pratiche" replace data-id="element-6083" data-mp="39zeiarpuhn9d" />;
  const update = (changes: Partial<PracticeDraft>) => {
    setDraft(prev => ({
      ...prev,
      ...changes
    }));
    setErrors(prev => {
      const next = {
        ...prev
      };
      Object.keys(changes).forEach(k => delete next[k]);
      if ('participants' in changes) delete next.credentials;
      if ('clientType' in changes) {
        delete next.companyName;
        delete next.firstName;
        delete next.lastName;
        delete next.businessName;
      }
      return next;
    });
  };
  const handleStatus = (status: PracticeStatus) => {
    update({
      status
    });
    // Positive outcome on a prospect client: ask to promote it to active
    if (status === 'Positivo' && draft.clientType === 'anagrafica' && draft.companyName && mockData.getClientByName(draft.companyName)?.status === 'Prospect') {
      setProspectName(draft.companyName);
    }
  };
  const activateClient = () => {
    if (!prospectName) return;
    mockData.setClientStatus(prospectName, 'Attivo');
    setActivatedNotice(`${prospectName} è ora un cliente attivo.`);
    setProspectName(null);
  };
  const validateScheduling = (): PracticeErrors => {
    const next: PracticeErrors = {};
    if (!draft.deliveryMode) {
      next.deliveryMode = 'Scegli il tipo di corso';
      return next;
    }
    if (!draft.courseCode) next.courseCode = 'Inserisci il codice corso';
    if (isInPresence(draft)) {
      if (!draft.courseStartDate) next.courseStartDate = 'Calendarizza il corso';
      if (draft.courseStartDate && draft.courseEndDate && draft.courseEndDate < draft.courseStartDate) next.courseEndDate = 'La data di fine non può precedere la data di inizio';
    } else {
      const valid = draft.participants.filter(p => p.firstName.trim() && p.lastName.trim() && p.email.trim());
      if (valid.length === 0 || valid.length !== draft.participants.length) next.participants = 'Inserisci nome, cognome ed email di ogni partecipante';else if (draft.participants.some(p => !p.credentialsSentAt)) next.credentials = 'Genera e invia le credenziali di accesso';
    }
    return next;
  };
  const confirmScheduling = () => {
    const next = validateScheduling();
    setErrors(prev => ({
      ...prev,
      ...next
    }));
    if (Object.keys(next).length === 0) update({
      schedulingConfirmed: true
    });
  };
  const validate = (): PracticeErrors => {
    const next: PracticeErrors = {};
    if (draft.clientType === 'anagrafica' && !draft.companyName) next.companyName = 'Seleziona la denominazione aziendale';
    if (draft.clientType === 'individuo') {
      if (!draft.firstName?.trim()) next.firstName = 'Inserisci il nome';
      if (!draft.lastName?.trim()) next.lastName = 'Inserisci il cognome';
    }
    if (draft.clientType === 'occasionale' && !draft.businessName?.trim()) next.businessName = 'Inserisci la ragione sociale';
    if (!draft.category) next.category = 'Seleziona la macrocategoria';
    if (!draft.productCode) next.productCode = 'Seleziona codice prodotto e tipologia corso';
    if (draft.status === 'Negativo') {
      if (!draft.rejectionDate) next.rejectionDate = 'Inserisci la data della non accettazione';
      if (!draft.rejectionReason?.trim()) next.rejectionReason = 'Inserisci la motivazione';
    }
    return next;
  };
  const handleSave = () => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    const isPositive = draft.status === 'Positivo';
    const saved = practicesStore.save({
      ...draft,
      // Keep only the data that applies to the chosen requester and status
      companyName: draft.clientType === 'anagrafica' ? draft.companyName : undefined,
      firstName: draft.clientType === 'individuo' ? draft.firstName : undefined,
      lastName: draft.clientType === 'individuo' ? draft.lastName : undefined,
      businessName: draft.clientType === 'occasionale' ? draft.businessName : undefined,
      // Individuals attend themselves: no attendee list
      attendees: draft.clientType === 'individuo' ? [] : draft.attendees || [],
      rejectionDate: draft.status === 'Negativo' ? draft.rejectionDate : undefined,
      rejectionReason: draft.status === 'Negativo' ? draft.rejectionReason : undefined,
      schedulingConfirmed: isPositive && draft.schedulingConfirmed,
      // Keep only the inputs of the chosen rate type
      hourlyRate: draft.rateType === 'oraria' ? draft.hourlyRate : undefined,
      courseHours: draft.rateType === 'oraria' ? draft.courseHours : undefined,
      singleFee: draft.rateType === 'singola' ? draft.singleFee : undefined
    });
    navigate('/training/pratiche', {
      state: {
        saved: existing ? 'updated' : 'created',
        id: saved.id
      }
    });
  };
  const errorCount = Object.keys(errors).length;
  const title = existing ? 'Modifica pratica' : source ? 'Duplica pratica' : 'Nuova pratica';
  return <Layout data-id="element-6084" data-mp="2lck4w6r7ial0">
      <Link to="/training/pratiche" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-seform-navy mb-4" data-id="element-6085" data-mp="2ldd14dsjhhow">
        <ArrowLeft className="h-4 w-4" data-id="element-6086" data-mp="2bl7eljl23sh3" /> Pratiche
      </Link>
      <div className="mb-6" data-id="element-6087" data-mp="2bjmktw6kgdka">
        <h1 className="text-3xl font-bold text-seform-navy" data-id="element-6088" data-mp="2zb1llkip2mtf">{title}</h1>
        {(existing || source) && <p className="text-slate-500 mt-1" data-id="element-6089" data-mp="34awcho2gzceh">
            {source && !existing ? 'Copia di: ' : ''}
            {requesterName(existing || source!)}
          </p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-id="element-6090" data-mp="2ubxer0j8t169">
        <div className="lg:col-span-2 space-y-6" data-id="element-6091" data-mp="1evpy09kjcm0m">
          <Card noHoverShadow data-id="element-6092" data-mp="1eux0lz7gv8v5">
            <PracticeMainFields draft={draft} onChange={update} errors={errors} data-id="element-6093" data-mp="1ext03ryyq5lp" />
            <div className="pt-6 border-t border-slate-100" data-id="element-6094" data-mp="1iyuwm11hw3jz">
              <PracticeStatusFields draft={draft} onStatusChange={handleStatus} onChange={update} errors={errors} data-id="element-6095" data-mp="2al4sznakiap9" />
              {activatedNotice && <p role="status" className="text-sm text-green-700 mt-3" data-id="element-6096" data-mp="21kq3scuhgbgh">
                  {activatedNotice}
                </p>}
            </div>
          </Card>

          {draft.status === 'Positivo' && <>
              <Card title="Processo programmazione corso" noHoverShadow data-id="element-6097" data-mp="2p9r4fdx2qib7">
                <PracticeScheduling draft={draft} onChange={update} errors={errors} onConfirm={confirmScheduling} data-id="element-6098" data-mp="2pbvjms0fb3qs" />
              </Card>
              <Card noHoverShadow data-id="element-6099" data-mp="3czd4s4p6n3ks">
                <PracticeBillingFields draft={draft} onChange={update} data-id="element-6100" data-mp="3d0er55bnfcht" />
              </Card>
            </>}
        </div>

        <aside className="lg:sticky lg:top-6 space-y-4" data-id="element-6101" data-mp="387bwsuz1rz0k">
          <Card title="Storico e azioni" noHoverShadow data-id="element-6102" data-mp="3fkky7lvk7j2l">
            <PracticeHistory history={draft.history} onAdd={entry => update({
            history: [...draft.history, entry]
          })} data-id="element-6103" data-mp="3fm4ehch7tu4d" />
          </Card>
          {errorCount > 0 && <div role="alert" className="flex gap-2 rounded-xl bg-red-50 border border-red-100 px-4 py-3" data-id="element-6104" data-mp="3tjkloq7c5rx6">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-6105" data-mp="2cggm0ur4fre" />
              <p className="text-sm text-red-700" data-id="element-6106" data-mp="7bko8n2125fm">
                {errorCount === 1 ? 'C’è 1 campo da correggere.' : `Ci sono ${errorCount} campi da correggere.`}
              </p>
            </div>}
          <div className="flex gap-3" data-id="element-6107" data-mp="h1co9wswbqrh">
            <Button variant="flat" color="default" className="flex-1" onClick={() => navigate('/training/pratiche')} data-id="element-6108" data-mp="l498tt1zxwy2">
              Annulla
            </Button>
            <Button className="flex-1" onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-6110" data-mp="3tzjw62evc0dp" />} data-id="element-6109" data-mp="3p106bqx5m4g2">
              {existing ? 'Salva modifiche' : 'Crea pratica'}
            </Button>
          </div>
        </aside>
      </div>

      <ProspectConfirmModal companyName={prospectName} onKeep={() => setProspectName(null)} onActivate={activateClient} data-id="element-6111" data-mp="7jfjc50gki43" />
    </Layout>;
}